import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import { Helmet } from 'react-helmet';
import {
  getSite,
  getSitePages,
  getSiteSubs,
  getTheme,
} from 'services/CommonServices';
import SiteSubGenerator from 'comp/SiteSubGenerator';
import AOS from 'aos';
import 'aos/dist/aos.css';
import './style.css';
import AdminSidebar from '../Common/AdminSidebar';
import { useBuilder } from '../../contexts/BuilderContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useInstance } from '../../contexts/InstanceContext';
import GlobalHeader from '../GlobalHeader';
import GlobalFooter from '../GlobalFooter';
import { ShadowContainer, NoShadowContainer, BorderedContainer, CleanContainer } from '../Layout';

const GlobalStyle = createGlobalStyle`
  body {
    background-color: #${(props) => props.theme.colors?.bodyColor || 'ffffff'};
    font-family: ${(props) => props.theme.font?.name || 'sans-serif'}, sans-serif;
  }
`;

// SiteContainer: Flex container to push footer to bottom
const SiteContainer = styled.div`
  transition: all 0.3s;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  flex: 1;
`;

// Wrapper that handles sidebar push effect - uses calc to account for Header navbar (40px)
const MainContentWrapper = styled.div`
  transition: margin-left 0.3s ease;
  margin-left: ${(props) => (props.sidebarOpen ? '400px' : '0')};
  min-height: calc(100dvh - 40px);

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

// Inner App component that can use BuilderContext
const AppContent = (props) => {
  const { globalHeaderType, globalFooterType } = props;
  const [site, setSite] = useState(props.site || { hasBackground: 0 });
  const [pages, setPages] = useState(null);
  const [theme, setTheme] = useState({ colors: {}, font: {} });

  const { isSidebarOpen, liveSiteSubs, updateLiveSiteSubs, initLiveSite } = useBuilder();
  const { layoutMode, fontSettings } = useTheme();
  const { memberId } = useInstance();

  // Generate Google Fonts URL from fontSettings (all categories)
  const getGoogleFontsUrl = () => {
    const fonts = new Set();
    // Global fonts
    if (fontSettings?.headings?.fontFamily) {
      fonts.add(fontSettings.headings.fontFamily);
    }
    if (fontSettings?.body?.fontFamily) {
      fonts.add(fontSettings.body.fontFamily);
    }
    // Hero section fonts
    if (fontSettings?.heroEnglish?.fontFamily) {
      fonts.add(fontSettings.heroEnglish.fontFamily);
    }
    if (fontSettings?.heroKorean?.fontFamily) {
      fonts.add(fontSettings.heroKorean.fontFamily);
    }
    if (fonts.size === 0) return null;

    const fontParams = Array.from(fonts)
      .map(font => `family=${font.replace(/ /g, '+')}:wght@300;400;500;600;700;800`)
      .join('&');
    return `https://fonts.googleapis.com/css2?${fontParams}&display=swap`;
  };
  const googleFontsUrl = getGoogleFontsUrl();

  useEffect(() => {
    if (!memberId) return;

    const fetchData = async () => {
      try {
        const siteResponse = await getSite(memberId);
        const siteData = siteResponse.data.data;
        setSite(siteData);
        initLiveSite(siteData); // BuilderContext에 site 초기화 (실시간 미리보기용)

        const themeId = siteData.themeId;
        if (themeId) {
          const themeResponse = await getTheme(themeId);
          const themeData = themeResponse.data?.data;
          if (themeData) {
            setTheme({
              colors: themeData.colors || {},
              font: themeData.font || {},
            });
          }
        }

        const pagesResult = await getSitePages(memberId);
        const fetchedPages = pagesResult.data.data;

        const pageQueries = fetchedPages.map(page => getSiteSubs(page.id));
        const pageValues = await Promise.all(pageQueries);

        const pagesWithSubs = fetchedPages.map(page => {
           const pageSubsResult = pageValues.find(value => Number(value.data.pageId) === Number(page.id));
           return {
             ...page,
             siteSubs: pageSubsResult ? (pageSubsResult.data.data || []) : []
           };
        });

        setPages(pagesWithSubs);

        // Initialize liveSiteSubs with first page's siteSubs
        if (pagesWithSubs.length > 0 && pagesWithSubs[0].siteSubs) {
          updateLiveSiteSubs(pagesWithSubs[0].siteSubs);
        }

      } catch (err) {
        console.error(err);
      }
    };

    fetchData();

    AOS.init();

    return () => {
      AOS.refresh();
    };
  }, [memberId, updateLiveSiteSubs]);

  // Get the siteSubs to display (prefer liveSiteSubs for real-time updates)
  const getDisplaySiteSubs = (page) => {
    // If we have liveSiteSubs, use them (for live editing preview)
    if (liveSiteSubs) {
      return liveSiteSubs;
    }
    // Otherwise fall back to page.siteSubs
    return page.siteSubs || [];
  };

  // Select container based on layout mode
  const getContentWrapper = () => {
    if (layoutMode === 'clean') {
      return CleanContainer; // No width restrictions, each section handles its own layout
    }
    // Bordered mode: use container with overflow: hidden
    return site.hasBackground ? ShadowContainer : BorderedContainer;
  };
  const ContentWrapper = getContentWrapper();
  const hasShadow = theme.colors?.hasShadow === 1;

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      {pages ? (
        <>
          <AdminSidebar site={site} page={pages[0]} setPages={setPages} />
          <MainContentWrapper sidebarOpen={isSidebarOpen}>
            <ContentWrapper hasShadow={hasShadow}>
              <SiteContainer className="App">
                {/* Global Header */}
                <GlobalHeader site={site} headerType={globalHeaderType} />

              {pages.length > 1 ? (
                <div className="container" style={{ flex: 1 }}>다중 페이지</div>
              ) : (
                <div style={{ flex: 1 }}>
                  {pages.map((page) => {
                    const displaySiteSubs = getDisplaySiteSubs(page);
                    return (
                      <React.Fragment key={page.id}>
                        <Helmet key="siteSEO">
                          {/* Google Fonts from theme settings */}
                          {googleFontsUrl && <link href={googleFontsUrl} rel="stylesheet" />}
                          {/* Legacy font URL from backend theme */}
                          {theme.font?.url && <link href={theme.font.url} rel="stylesheet" />}
                          {page.seo ? (
                            page.seo.map((seoElement, index) => {
                              if (seoElement.name === 'title')
                                return <title key={index}>{seoElement.content}</title>;
                              else if (seoElement.name === 'favicon')
                                return (
                                  <link
                                    key={index}
                                    rel="icon"
                                    type="image/png"
                                    sizes="32x32"
                                    href={seoElement.content}
                                  />
                                );
                              // Open Graph uses 'property', Twitter and others use 'name'
                              const isOg = seoElement.name.startsWith('og:');
                              return (
                                <meta
                                  key={index}
                                  {...(isOg ? { property: seoElement.name } : { name: seoElement.name })}
                                  content={seoElement.content}
                                />
                              );
                            })
                          ) : (
                            <title>Loading...</title>
                          )}
                        </Helmet>
                        {displaySiteSubs
                          .filter((siteSub) => !siteSub.type.includes('header') && !siteSub.type.includes('footer'))
                          .map((siteSub) => (
                            <SiteSubGenerator key={siteSub.id} site={site} siteSub={siteSub} />
                          ))
                        }
                      </React.Fragment>
                    );
                  })}
                </div>
              )}

              {/* Global Footer */}
              <GlobalFooter site={site} footerType={globalFooterType} />
              </SiteContainer>
            </ContentWrapper>
          </MainContentWrapper>
        </>
      ) : (
        <MainContentWrapper sidebarOpen={isSidebarOpen}>
          <NoShadowContainer>
            <SiteContainer className="App">
              <header className="App-header">Loading...</header>
            </SiteContainer>
          </NoShadowContainer>
        </MainContentWrapper>
      )}
    </ThemeProvider>
  );
};

const App = (props) => {
  // BuilderProvider is already provided in index.js - don't wrap again here
  return <AppContent {...props} />;
};

export default App;
